/*
 * Object URLs for an article's attachments, for one consumer - the editor's
 * preview, or the image list's thumbnails - read from a cache the whole page
 * shares.
 *
 * An <img> cannot present a bearer token, and the whole admin router is behind
 * requireAdmin - so the bytes are fetched with the token and handed to the
 * element as a blob: URL. Every one of those holds its bytes in memory until it
 * is revoked, which is why the cache lives in one place.
 *
 * SHARED, NOT PER CONSUMER (2026-09-21). This hook used to own its cache, so the
 * preview and the thumbnails each downloaded the same picture. It now registers
 * what its caller wants with the page's ArticleImagePreviewStore - see
 * ArticleImagePreviewsProvider, which the editor page wraps both consumers in -
 * and reads the store's record; the store fetches the union once and releases an
 * entry only when no consumer wants it. Without a provider covering the article,
 * the hook makes a private store of its own, so it still works alone and its
 * tests still pin the single-consumer contract.
 *
 * `enabled` false PAUSES this consumer rather than releasing what it registered:
 * the editor switches it off in write mode, and what the preview had loaded
 * should still be there when the author switches back.
 *
 * ANY OWNER (S42, 2026-10-12). `useImagePreviews` and `useOwnedImagePreviewStore`
 * take an owner - a namespaced key and a fetch function, see imageOwner.ts - so a
 * release note's editor gets the same cache. A store is keyed on the owner's KEY
 * and reads the fetch through a ref: an adapter object rebuilt by a render must
 * not dispose the store, revoke every URL and download every picture again. The
 * article signatures are kept as thin wrappers, so the article editor and these
 * hooks' own tests are unchanged.
 */

import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ArticleImage } from '@accotool-kb/shared';
import type { AdminApi } from '../../api/adminApi.js';
import { ArticleImagePreviewStore, articleImagePreviewEntry } from './articleImagePreviewStore.js';
import { articleImageOwnerKey, type ImagePreviewSource } from './imageOwner.js';

/** The page-level store, when a provider covers this part of the tree. */
export const ArticleImagePreviewsContext = createContext<ArticleImagePreviewStore | null>(null);

const EMPTY: Record<string, string> = {};
const noSubscription = (): (() => void) => () => undefined;
const emptySnapshot = (): Record<string, string> => EMPTY;

/**
 * An article's id as a preview source, or null without one. Built per call - the
 * hooks below key on `key` and read `objectUrl` through a ref, so a fresh object
 * per render costs nothing.
 */
function articlePreviewSource(
  api: AdminApi,
  articleId: string | undefined,
): ImagePreviewSource | null {
  if (!articleId) return null;
  return {
    key: articleImageOwnerKey(articleId),
    objectUrl: (fileName) => api.articleImageObjectUrl(articleId, fileName),
  };
}

/**
 * A store this component owns: created when the owner is known, disposed -
 * every URL released - when the owner's KEY changes or the component unmounts.
 * Created inside the effect rather than memoised, so StrictMode's simulated
 * unmount disposes one instance and the real mount gets a fresh one instead of a
 * disposed one.
 *
 * The fetch is read through a ref at call time, never captured at creation: the
 * owner's functions belong to whatever render last supplied them, and only a
 * change of key - a different owner - is a reason for a new store.
 */
export function useOwnedImagePreviewStore(
  owner: ImagePreviewSource | null | undefined,
): ArticleImagePreviewStore | null {
  const [store, setStore] = useState<ArticleImagePreviewStore | null>(null);

  const fetchRef = useRef(owner?.objectUrl);
  fetchRef.current = owner?.objectUrl;
  const key = owner?.key;

  useEffect(() => {
    if (!key) {
      setStore(null);
      return undefined;
    }
    const created = new ArticleImagePreviewStore((fileName) => {
      const fetch = fetchRef.current;
      return fetch ? fetch(fileName) : Promise.reject(new Error('No image owner.'));
    }, key);
    setStore(created);
    return () => {
      created.dispose();
      setStore((current) => (current === created ? null : current));
    };
  }, [key]);

  return store;
}

/** The article form of useOwnedImagePreviewStore, kept for its callers and tests. */
export function useOwnedArticleImagePreviewStore(
  api: AdminApi,
  articleId: string | undefined,
): ArticleImagePreviewStore | null {
  return useOwnedImagePreviewStore(articlePreviewSource(api, articleId));
}

/**
 * Maps file name to object URL for the images given, fetching what the page
 * does not already hold and releasing what nothing on the page wants any more.
 *
 * A name is absent from the result while its bytes are in flight, and stays
 * absent if the fetch failed - callers decide what to show in the meantime.
 * `contentHash` is part of the cache key, so replacing an image's bytes under
 * the same file name re-fetches rather than showing the old picture.
 */
export function useArticleImagePreviews(
  api: AdminApi,
  articleId: string | undefined,
  images: readonly ArticleImage[],
  enabled: boolean,
): Record<string, string> {
  return useImagePreviews(articlePreviewSource(api, articleId), images, enabled);
}

/**
 * useArticleImagePreviews for any owner - an article or a release note. Uses the
 * page's store when a provider covers the SAME owner (by key), otherwise a
 * private store of its own; null owner, nothing fetched.
 */
export function useImagePreviews(
  owner: ImagePreviewSource | null | undefined,
  images: readonly ArticleImage[],
  enabled: boolean,
): Record<string, string> {
  const shared = useContext(ArticleImagePreviewsContext);
  const covered = shared !== null && owner != null && shared.ownerKey === owner.key;
  const own = useOwnedImagePreviewStore(covered ? null : owner);
  const store = covered ? shared : own;

  // This consumer's identity in the store, for the life of the component.
  const consumer = useRef<symbol | null>(null);
  if (consumer.current === null) consumer.current = Symbol('article image previews consumer');
  const id = consumer.current;

  // A primitive dependency, so the effect re-runs when the set of wanted images
  // changes and not merely because the array was rebuilt by a render.
  const wantedKey = images
    .map((image) => articleImagePreviewEntry(image.fileName, image.contentHash))
    .sort()
    .join('|');

  useEffect(() => {
    if (!store || !enabled) return;
    store.want(id, new Set(wantedKey.length > 0 ? wantedKey.split('|') : []));
  }, [store, enabled, wantedKey, id]);

  // Leaving releases what only this consumer wanted - and only on leaving, not
  // on every change of the wanted set, or an image in flight would be dropped
  // and re-requested each time a row scrolled into view.
  useEffect(() => {
    if (!store) return undefined;
    return () => store.forget(id);
  }, [store, id]);

  return useSyncExternalStore(
    store ? store.subscribe : noSubscription,
    store ? store.getSnapshot : emptySnapshot,
  );
}
